export interface ProductPageData {
  slug: string;
  name: string;
  tagline: string;
  headline: string;
  subheadline: string;
  heroSvg?: string;
  problemStats: Array<{
    value: string;
    label: string;
  }>;
  problemHeadline: string;
  problemDescription: string;
  sections: Array<{
    title: string;
    description: string;
    details: string[];
    imagePosition: 'left' | 'right';
    svg?: string;
  }>;
  useCases: Array<{
    industry: string;
    description: string;
  }>;
  ctaHeadline: string;
  ctaDescription: string;
  relatedSlugs: string[];
}

export const productPages: ProductPageData[] = [
  {
    slug: 'qr-nfc-tags',
    name: 'QR & NFC Tags',
    tagline: 'Product Identity',
    headline: 'Give every product a digital identity',
    subheadline: 'Branded QR codes and NFC tags that turn packaging into a direct channel to your customers. Each tag is serial-tracked, unique, and connected to a full product experience.',
    heroSvg: '/img/features/qr-scan-flow.svg',
    problemStats: [
      { value: '92%', label: 'of manufacturers have no direct customer relationship' },
      { value: '0', label: 'data points captured from a paper manual' },
      { value: '$0', label: 'aftermarket revenue from unconnected products' },
    ],
    problemHeadline: 'Your packaging is a wasted channel',
    problemDescription: 'Every product you ship is an opportunity to start a relationship. Instead, customers get a paper manual they throw away, a warranty card they never send back, and zero reason to stay connected with your brand.',
    sections: [
      {
        title: 'Serial-tracked QR codes at scale',
        description: 'Generate unique QR codes for every individual product — not just per SKU, but per unit. Each code links to a dynamic experience that knows exactly which product, batch, and serial number was scanned.',
        details: [
          'Unique codes per unit with serial number encoding',
          'GS1 Digital Link compliant for retail and regulatory scanning',
          'Custom branded designs with your logo and colors',
          'SVG and DataMatrix output for any print workflow',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'NFC tags for premium experiences',
        description: 'Embed NFC chips in packaging or on the product itself. Customers tap to connect — no camera, no app, no friction. Perfect for luxury goods, high-value equipment, and products where the unboxing matters.',
        details: [
          'Tap-to-connect with any modern smartphone',
          'Works through packaging materials',
          'Tamper-evident options for authentication',
          'Combined QR + NFC for maximum accessibility',
        ],
        imagePosition: 'left' as const,
      },
      {
        title: 'Built-in label printing',
        description: 'Design labels with drag-and-drop, configure sheet layouts, and print directly from BrandedMark. Batch print thousands of serialized labels for production lines or generate singles for service replacements.',
        details: [
          'Drag-and-drop label template designer',
          'Sheet layout for multi-label printing',
          'Cross-platform print client (Windows, macOS, Linux)',
          'Batch generation for production-scale runs',
        ],
        imagePosition: 'right' as const,
      },
    ],
    useCases: [
      { industry: 'Consumer Electronics', description: 'Printed on packaging, links to setup guides and warranty activation' },
      { industry: 'Appliances', description: 'Permanently affixed labels with NFC for lifetime product support' },
      { industry: 'Industrial Equipment', description: 'Asset-level tracking with GS1 Digital Link compliance' },
      { industry: 'Luxury Goods', description: 'NFC authentication tags proving provenance and ownership' },
    ],
    ctaHeadline: 'Turn every product into a connected experience',
    ctaDescription: 'Start generating serial-tracked QR codes and connecting with your customers today.',
    relatedSlugs: ['product-experiences', 'warranty', 'compliance-dpp'],
  },
  {
    slug: 'product-experiences',
    name: 'Product Experiences',
    tagline: 'Experience Designer',
    headline: 'Build product experiences without code',
    subheadline: 'A no-code visual builder for the pages your customers see when they scan. Setup guides, manuals, videos, registration — all browser-based, no app download required.',
    problemStats: [
      { value: '73%', label: 'of customers prefer self-service over contacting support' },
      { value: '60%', label: 'of paper manuals are never read' },
      { value: '4.2M', label: 'apps on app stores — yours won\'t get downloaded' },
    ],
    problemHeadline: 'Apps fail. Paper manuals fail. Websites are generic.',
    problemDescription: 'Building an app for your product costs millions and nobody downloads it. Paper manuals get lost. Your website shows the same generic content to everyone. Your customers deserve product experiences that are specific, contextual, and instant.',
    sections: [
      {
        title: 'Visual drag-and-drop builder',
        description: 'Design the exact experience your customers should have when they scan a product. Drag components onto the canvas, configure conditional logic, and publish — all without writing a line of code.',
        details: [
          '12 experience areas: Setup, Registration, Support, Spares, DPP, and more',
          'Component library with versioning',
          'Show/When conditional logic per page',
          'Draft to Published versioning with full history',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'Context-aware content',
        description: 'Every scan carries context: which product, which serial number, what warranty status, what language. BrandedMark uses that context to show exactly the right content to each customer.',
        details: [
          'Auto-detected product, serial, and warranty status',
          'Language-aware content routing',
          'Conditional pages based on registration or warranty state',
          'Shared components across products for consistent branding',
        ],
        imagePosition: 'left' as const,
      },
      {
        title: 'Fully browser-based, zero friction',
        description: 'No app downloads. No account creation required. Customers scan and they\'re immediately in a branded experience that works on any device. The web is the platform.',
        details: [
          'Works on any smartphone browser — iOS and Android',
          'Progressive enhancement for low-bandwidth connections',
          'Branded with your logo, colors, and styling',
          'ActionSheet overlay navigation for mobile-native feel',
        ],
        imagePosition: 'right' as const,
      },
    ],
    useCases: [
      { industry: 'Home Appliances', description: 'Interactive setup guides that replace 40-page paper manuals' },
      { industry: 'Fitness Equipment', description: 'Video tutorials and maintenance schedules specific to each model' },
      { industry: 'Outdoor Gear', description: 'Care instructions, warranty info, and accessories in one scan' },
      { industry: 'Rental Equipment', description: 'In-store demo experiences that show product capabilities' },
    ],
    ctaHeadline: 'Design the experience your products deserve',
    ctaDescription: 'Build beautiful, contextual product experiences in minutes — no developers needed.',
    relatedSlugs: ['qr-nfc-tags', 'ai-support', 'warranty'],
  },
  {
    slug: 'warranty',
    name: 'Warranty & Registration',
    tagline: 'After-Sales',
    headline: 'Warranty that works for everyone',
    subheadline: 'Customers register in 30 seconds. You get first-party data, fraud detection, and multi-jurisdiction compliance — all automated.',
    heroSvg: '/img/features/warranty-registration.svg',
    problemStats: [
      { value: '<5%', label: 'warranty registration rate with paper cards' },
      { value: '$15', label: 'average cost per inbound support call' },
      { value: '17+', label: 'jurisdictions with different statutory warranty rules' },
    ],
    problemHeadline: 'Paper warranty cards are dead',
    problemDescription: 'Less than 5% of customers return paper warranty cards. That means 95% of your customers are invisible to you. You have no data on who bought your product, when, or where. And when they need support, you have no context.',
    sections: [
      {
        title: '30-second digital registration',
        description: 'Customers scan the QR code on their product and register in seconds. The serial number is auto-detected. The form is pre-filled where possible. You capture name, email, purchase date, and location — building a direct customer database.',
        details: [
          'Auto-detected serial number from QR/NFC scan',
          'Minimal-friction form with smart defaults',
          'Instant warranty activation confirmation',
          'First-party customer data captured at registration',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'Claims management end-to-end',
        description: 'When a customer has a warranty issue, they submit a claim through the same product experience. You manage the full lifecycle: review, approve, repair, return, refund — all in one place.',
        details: [
          'Claim submission with photo evidence',
          'Status machine: Draft, Review, Resolved, Cancelled, Closed',
          'RMA lifecycle with service center assignment',
          'Automated refunds via Stripe on RMA completion',
        ],
        imagePosition: 'left' as const,
      },
      {
        title: 'Multi-jurisdiction compliance',
        description: 'BrandedMark knows that warranty law in Scotland is different from England, that EU statutory conformity periods vary, and that Australian Consumer Law has its own rules. The warranty engine handles all of this automatically.',
        details: [
          '17+ jurisdictions seeded (EU-27, GB, US, AU, JP, BR, CA, and more)',
          'Rome I Art. 6 priority resolution for cross-border sales',
          'Statutory rights always enforced alongside commercial warranty',
          'GDPR-aware data collection with jurisdiction-specific consent',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'Fraud detection and rule engine',
        description: 'Not every registration is genuine. BrandedMark detects duplicate registrations, rapid-fire submissions, and suspicious patterns — while a configurable rule engine lets you auto-approve, block, or flag registrations based on your criteria.',
        details: [
          'Duplicate and rapid registration signal detection',
          'Configurable rule engine with AND/OR conditions',
          'Actions: AutoApprove, Block, WaiveRestockingFee, RequirePhotos',
          'Pending registration approval workflow',
        ],
        imagePosition: 'left' as const,
      },
    ],
    useCases: [
      { industry: 'Consumer Electronics', description: 'Warranty activation at unboxing with auto-detected serial numbers' },
      { industry: 'Home Appliances', description: 'Extended warranty upsell at registration with Stripe payments' },
      { industry: 'Power Tools', description: 'Professional vs consumer warranty tiers based on registration type' },
      { industry: 'HVAC', description: 'Installer verification required before warranty activation' },
    ],
    ctaHeadline: 'Stop losing 95% of your customers',
    ctaDescription: 'Replace paper warranty cards with instant digital registration that builds your customer database.',
    relatedSlugs: ['qr-nfc-tags', 'returns-repairs', 'analytics'],
  },
  {
    slug: 'ai-support',
    name: 'AI Support Agent',
    tagline: 'Intelligent Support',
    headline: 'AI that actually knows your products',
    subheadline: 'An AI support agent trained on your product manuals, with access to warranty status, order history, and real-time inventory. Resolves queries in seconds, not days.',
    heroSvg: '/img/features/ai-support.svg',
    problemStats: [
      { value: '$15', label: 'average cost per support call to a human agent' },
      { value: '72hrs', label: 'typical email support response time' },
      { value: '40%', label: 'of support queries are the same 10 questions' },
    ],
    problemHeadline: 'Generic chatbots don\'t know your products',
    problemDescription: 'Off-the-shelf chatbots give generic answers. Your customers don\'t want generic — they want to know why their specific model is showing error code E04, whether their warranty covers it, and where to get the replacement part. That requires product-specific intelligence.',
    sections: [
      {
        title: 'Product-aware AI with grounded retrieval',
        description: 'The AI agent is trained on your product documentation and uses retrieval-augmented generation to give accurate, grounded answers. It knows the specific product the customer scanned, the serial number, and the full support context.',
        details: [
          'Knowledge base entries per product for AI context',
          'Grounded retrieval from your actual manuals and documentation',
          'Product-specific answers, not generic responses',
          'Context enrichers: product, tag, conversation history, escalation',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'Real tools, not just chat',
        description: 'This isn\'t a chatbot that just talks. The AI agent can check inventory, look up orders, verify warranty status, and even create warranty claims — all within the conversation.',
        details: [
          'CheckInventory, GetOrderDetails, GetWarrantyClaimDetails',
          'LookupUserOrders, ListRmas, ListWarrantyClaims',
          'CreateWarrantyClaim directly from conversation',
          'RequestVerification for sensitive operations',
        ],
        imagePosition: 'left' as const,
      },
      {
        title: 'Auto-escalation and learning',
        description: 'When the AI can\'t resolve an issue, it escalates to your human support team with full conversation context. And it learns: resolved conversations feed back into the system to improve future responses.',
        details: [
          'Automatic escalation to human agents when needed',
          'Full conversation context passed to support staff',
          'Resolution learning service improves over time',
          'Support analytics and AI optimization suggestions',
        ],
        imagePosition: 'right' as const,
      },
    ],
    useCases: [
      { industry: 'Home Appliances', description: 'Error code troubleshooting with step-by-step guided repair' },
      { industry: 'Consumer Electronics', description: 'Setup assistance that knows the exact firmware and model variant' },
      { industry: 'Industrial Equipment', description: 'Maintenance scheduling and parts ordering within the conversation' },
      { industry: 'Any Product', description: 'Instant answers to "how do I..." questions from your product manual' },
    ],
    ctaHeadline: 'Support that scales without headcount',
    ctaDescription: 'Deploy an AI agent that knows your products as well as your best support engineer.',
    relatedSlugs: ['product-experiences', 'warranty', 'spares-accessories'],
  },
  {
    slug: 'spares-accessories',
    name: 'Spares & Accessories',
    tagline: 'Commerce',
    headline: 'Capture the aftermarket revenue you\'re losing',
    subheadline: 'Surface the right spare parts and accessories at the right moment — directly from the product scan. No more customers buying third-party replacements from Amazon.',
    problemStats: [
      { value: '$400B', label: 'global aftermarket parts market' },
      { value: '70%+', label: 'of aftermarket revenue goes to third-party sellers' },
      { value: '3.2x', label: 'higher conversion when shown contextually relevant parts' },
    ],
    problemHeadline: 'Your aftermarket revenue is leaking',
    problemDescription: 'When a customer needs a replacement filter, a new blade, or a compatible accessory, they search on Amazon or Google. They buy a third-party part at half the price. You lose the sale, the margin, and the relationship. The customer doesn\'t even know you sell parts.',
    sections: [
      {
        title: 'Product-specific spares catalog',
        description: 'Link spare parts and accessories directly to parent products. When a customer scans their product, they see exactly the parts that fit — no searching, no compatibility guessing, no wrong orders.',
        details: [
          'Spares and accessories linked per product/SKU',
          'Consumer-facing purchase flow embedded in product experience',
          'Multi-market pricing with currency support',
          'Import/export for bulk catalog management',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'Full commerce engine built in',
        description: 'Not just a catalog — a complete commerce flow. Cart, checkout, tax calculation, shipping, fulfillment, invoicing. Stripe payments with multi-currency support. Everything needed to sell direct.',
        details: [
          'Basket to order conversion with tax calculation',
          'Shipping profiles with zones, rates, and conditions',
          'Invoice and credit note generation (PDF)',
          'Abandoned checkout detection and recovery',
        ],
        imagePosition: 'left' as const,
      },
      {
        title: 'AI-powered recommendations',
        description: 'The AI support agent can recommend parts during a troubleshooting conversation, check inventory in real time, and guide the customer through purchase — turning support interactions into revenue.',
        details: [
          'AI agent CheckInventory tool for real-time stock',
          'Contextual part recommendations during support conversations',
          'Accessory suggestions based on product model',
          'Order lookup and tracking for existing purchases',
        ],
        imagePosition: 'right' as const,
      },
    ],
    useCases: [
      { industry: 'Home Appliances', description: 'Replacement filters, bulbs, and consumables sold directly from the product' },
      { industry: 'Power Tools', description: 'Blades, batteries, and accessories surfaced when scanned at point of use' },
      { industry: 'Coffee Machines', description: 'Descaler, filters, and pod subscriptions from a QR code on the machine' },
      { industry: 'Outdoor Equipment', description: 'Seasonal maintenance kits and upgrade accessories' },
    ],
    ctaHeadline: 'Own your aftermarket',
    ctaDescription: 'Start selling spares and accessories directly from your products.',
    relatedSlugs: ['product-experiences', 'ai-support', 'analytics'],
  },
  {
    slug: 'returns-repairs',
    name: 'Returns & Repairs',
    tagline: 'Service Management',
    headline: 'Returns and repairs, visible to everyone',
    subheadline: 'Track repair requests, manage returns, assign work to service centers — with full customer visibility at every step. No more "where\'s my repair?" calls.',
    problemStats: [
      { value: '30%', label: 'of support calls are "where\'s my repair?" status checks' },
      { value: '$33', label: 'average cost to process a product return' },
      { value: '62%', label: 'of customers won\'t buy again after a bad returns experience' },
    ],
    problemHeadline: 'Returns are where you lose customers forever',
    problemDescription: 'A customer\'s product breaks. They email your support team. Nobody knows the warranty status. The repair takes weeks with zero visibility. The customer calls three times for status updates. Each call costs $15. By the time they get the product back, they\'ve already bought a competitor\'s replacement.',
    sections: [
      {
        title: 'RMA lifecycle management',
        description: 'Create, track, and complete return merchandise authorizations from a single dashboard. Every RMA has a clear status: Draft, Open, AwaitingReceipt, Received, Completed. No ambiguity.',
        details: [
          'Full RMA lifecycle with clear status machine',
          'Service center assignment and tracking',
          'Photo evidence capture at each stage',
          'Automated Stripe refunds on RMA completion',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'Customer-visible tracking',
        description: 'Customers see their repair status in real time through the same product experience they used to register. No need to call, no need to email. They scan their product and see exactly where things stand.',
        details: [
          'Real-time status visible from product scan',
          'Automatic notification on status changes',
          'Timeline of all events visible to customer',
          'Reduces "where\'s my repair?" calls by up to 80%',
        ],
        imagePosition: 'left' as const,
      },
      {
        title: 'Warranty-integrated decisions',
        description: 'Every return and repair decision is informed by warranty status. Is the product in warranty? Is there a restocking fee? Does this claim require photos? The rule engine handles it automatically.',
        details: [
          'Warranty status checked automatically on claim',
          'Restocking fee calculator with rule-based waivers',
          'Photo requirement evaluator',
          'Fraud detection signals on suspicious claims',
        ],
        imagePosition: 'right' as const,
      },
    ],
    useCases: [
      { industry: 'Consumer Electronics', description: 'RMA with courier collection and repair tracking' },
      { industry: 'Appliances', description: 'On-site repair scheduling with service center assignment' },
      { industry: 'Furniture', description: 'Replacement part fulfillment instead of full returns' },
      { industry: 'B2B Equipment', description: 'Multi-unit returns with batch RMA creation' },
    ],
    ctaHeadline: 'Make returns your competitive advantage',
    ctaDescription: 'Turn a pain point into a loyalty driver with transparent, efficient returns and repairs.',
    relatedSlugs: ['warranty', 'ai-support', 'analytics'],
  },
  {
    slug: 'compliance-dpp',
    name: 'Compliance & DPP',
    tagline: 'Digital Product Passport',
    headline: 'EU Digital Product Passport, built in',
    subheadline: 'ESPR compliance isn\'t a bolt-on. BrandedMark generates Digital Product Passports from data you already have — product identity, materials, repairability, environmental impact, and end-of-life recycling.',
    heroSvg: '/img/features/digital-product-passport.svg',
    problemStats: [
      { value: '2027', label: 'EU Digital Product Passport deadline for key categories' },
      { value: '30+', label: 'data fields required per product under ESPR' },
      { value: '100%', label: 'of products sold in the EU will need a DPP' },
    ],
    problemHeadline: 'ESPR compliance is coming — and most brands aren\'t ready',
    problemDescription: 'The EU Ecodesign for Sustainable Products Regulation (ESPR) requires a Digital Product Passport for every product sold in the EU. That means a publicly accessible, machine-readable dataset covering materials, repairability, carbon footprint, and end-of-life. Most brands don\'t even have this data centralized, let alone published.',
    sections: [
      {
        title: '5-page DPP auto-generated',
        description: 'BrandedMark auto-seeds five standard DPP pages for every product: Product Identity, Materials & Composition, Repairability & Spares, Environmental & Lifecycle, and End-of-Life & Recycling. Fill in the data, publish, and you\'re compliant.',
        details: [
          'Product Identity: manufacturer, model, GTIN, serial, production date',
          'Materials: recycled content, REACH compliance, SVHC declarations',
          'Repairability: score, spare parts availability guarantees',
          'Environmental: carbon footprint, energy rating, expected lifespan',
          'End-of-Life: WEEE category, disassembly guide, recyclability percentage',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'GS1 Digital Link resolution',
        description: 'BrandedMark resolves GS1 Digital Links natively. Scan a barcode, GTIN, or QR code and the system routes to the correct product experience and DPP — as required by EU regulation and GS1 Sunrise 2027.',
        details: [
          'Full SGTIN parsing and URI construction',
          'Per-tenant GS1 company prefix configuration',
          'Dynamic tag creation from retail barcode scans',
          'Privacy-preserving SGTIN hashing for external indices',
        ],
        imagePosition: 'left' as const,
      },
      {
        title: 'Anonymous public access',
        description: 'EU regulation requires that DPP data is accessible without login or authentication. BrandedMark serves DPP data through anonymous REST endpoints — anyone can scan and access compliance data, as the law requires.',
        details: [
          'Anonymous REST endpoint per EU regulation',
          'No login required for DPP access',
          'Machine-readable data format for regulatory bodies',
          'DPP compliance signal tracking and reporting',
        ],
        imagePosition: 'right' as const,
      },
    ],
    useCases: [
      { industry: 'Electronics', description: 'Full ESPR compliance for batteries, electronics, and ICT products' },
      { industry: 'Textiles', description: 'Material composition, recycled content, and care instructions' },
      { industry: 'Appliances', description: 'Energy ratings, repairability scores, and spare parts guarantees' },
      { industry: 'Construction Products', description: 'CE declarations, environmental data, and end-of-life recycling info' },
    ],
    ctaHeadline: 'Be ready before the deadline',
    ctaDescription: 'Start building your Digital Product Passports now — from the data you already have.',
    relatedSlugs: ['qr-nfc-tags', 'product-experiences', 'analytics'],
  },
  {
    slug: 'analytics',
    name: 'Analytics & Customers',
    tagline: 'Intelligence',
    headline: 'Finally see who uses your products',
    subheadline: 'Track every scan, registration, and interaction across your product range. Build a direct customer database. Export, analyze, and act on real post-purchase data.',
    problemStats: [
      { value: '92%', label: 'of manufacturers can\'t identify their end customers' },
      { value: '0', label: 'post-purchase data points from traditional distribution' },
      { value: '5x', label: 'cheaper to retain a customer than acquire a new one' },
    ],
    problemHeadline: 'You\'re flying blind after the sale',
    problemDescription: 'You ship products to distributors. They ship to retailers. Retailers sell to consumers. You have no idea who your customers are, where they are, or what they need. Every product insight you have stops at the warehouse door.',
    sections: [
      {
        title: 'Scan and engagement analytics',
        description: 'See when, where, and how often your products are being scanned. Track registration rates, support engagement, and commerce conversion. Understand your product lifecycle in real time.',
        details: [
          'Scan tracking with geographic and device data',
          'Registration conversion rates per product and channel',
          'Support engagement and resolution metrics',
          'Commerce and spares revenue attribution',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'Customer database you own',
        description: 'Every registration builds your first-party customer database. Names, emails, locations, purchase dates — direct data you own, not rented from a platform. GDPR-compliant with jurisdiction-aware consent.',
        details: [
          'People and company records with full address book',
          'Customer provisioning on registration or purchase',
          'GDPR personal data anonymization',
          'Filtered dashboards for segmentation and outreach',
        ],
        imagePosition: 'left' as const,
      },
      {
        title: 'Export and integrate',
        description: 'Your data shouldn\'t be locked in. Export to CSV, connect via REST API, or use the MCP server to query data from AI assistants like Claude or Cursor. Your customer data, your way.',
        details: [
          'CSV export for products, customers, orders, and more',
          'REST API with 19 read scopes and date-based versioning',
          'MCP server for AI assistant integration',
          '17 webhook event types for real-time integrations',
        ],
        imagePosition: 'right' as const,
      },
    ],
    useCases: [
      { industry: 'Consumer Brands', description: 'Build direct customer relationships through product registration data' },
      { industry: 'Distributors', description: 'Understand sell-through rates and end-customer geography' },
      { industry: 'Product Teams', description: 'Identify most-scanned products, common support queries, parts demand' },
      { industry: 'Marketing', description: 'First-party data for retargeting, upsell, and lifecycle campaigns' },
    ],
    ctaHeadline: 'Know your customers, not just your distributors',
    ctaDescription: 'Start building your first-party customer database from product interactions.',
    relatedSlugs: ['warranty', 'qr-nfc-tags', 'spares-accessories'],
  },
  // ── Solution-level pages (combine product areas by outcome) ──
  {
    slug: 'product-identity',
    name: 'Product Identity',
    tagline: 'Know Every Product. Know Every Owner.',
    headline: 'Give every product a digital life',
    subheadline: 'A unique digital identity for every unit you manufacture — serialized QR codes, NFC tags, GS1 Digital Link compliance, and cryptographic authentication. The foundation everything else is built on.',
    problemStats: [
      { value: '92%', label: 'of product owners are invisible to the brand that made the product' },
      { value: '$509B', label: 'in annual counterfeiting losses globally' },
      { value: '10-28%', label: 'warranty registration rate with paper cards' },
    ],
    problemHeadline: 'After the sale, your products disappear',
    problemDescription: 'You ship thousands of products. You know nothing about who buys them, where they end up, or what happens after unboxing. Retailers own the customer data. Amazon sells your spare parts. And counterfeiters copy your packaging. Product identity changes all of this — one QR scan creates a direct, authenticated relationship between your brand and every product owner.',
    sections: [
      {
        title: 'Serialized QR codes at scale',
        description: 'Every unit gets a unique identity — not per SKU, but per individual product. GS1 Digital Link compliant, branded with your logo, and generated at production scale. One scan links the customer to their specific product.',
        details: [
          'Unique serial number per unit (SGTIN format)',
          'GS1 Digital Link compliant for retail and regulatory scanning',
          'Custom branded QR designs with logo',
          'Batch generation for production-scale runs',
          'SVG and DataMatrix output for any print workflow',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'Cryptographic authentication',
        description: 'Passkey-based product ownership using FIDO2/WebAuthn. No passwords, no accounts — biometric authentication proves who owns the product. Counterfeiters can copy a QR code. They cannot copy a cryptographic key.',
        details: [
          'FIDO2/WebAuthn passkey registration',
          'Biometric authentication (face, fingerprint, device PIN)',
          'Ownership transfer with cryptographic proof',
          'Anti-counterfeiting via serial validation',
          'Apple Wallet and Google Wallet integration',
        ],
        imagePosition: 'left' as const,
      },
      {
        title: 'NFC for premium experiences',
        description: 'Tap-to-connect with any modern smartphone. NFC tags embedded in packaging or products enable instant, frictionless access to the product experience — no camera, no app download required.',
        details: [
          'Works through packaging materials',
          'Tamper-evident options for luxury authentication',
          'Combined QR + NFC for maximum accessibility',
          'Premium unboxing experience trigger',
        ],
        imagePosition: 'right' as const,
      },
    ],
    useCases: [
      { industry: 'Consumer Electronics', description: 'Serial-level tracking, warranty activation at unboxing, counterfeit detection' },
      { industry: 'Luxury Goods', description: 'NFC authentication, provenance verification, resale value protection' },
      { industry: 'Industrial Equipment', description: 'Asset tracking, installer certification, maintenance history per unit' },
      { industry: 'FMCG', description: 'Anti-counterfeiting at scale, recall precision, consumer engagement campaigns' },
    ],
    ctaHeadline: 'Every product deserves a digital identity',
    ctaDescription: 'Start with one product line. Generate serialized QR codes. See your first registrations within days.',
    relatedSlugs: ['compliance-dpp', 'warranty', 'analytics'],
  },
  {
    slug: 'digital-product-passport',
    name: 'Digital Product Passport',
    tagline: 'EU-Ready. Day One.',
    headline: 'DPP compliance without the enterprise budget',
    subheadline: 'Battery DPP is enforced. Textiles and electronics are next. BrandedMark gives you the infrastructure to comply — and the platform to turn compliance into competitive advantage.',
    problemStats: [
      { value: '84%', label: 'of UK businesses feel unprepared for DPP requirements' },
      { value: 'Jan 2026', label: 'Battery DPP enforcement went live' },
      { value: '2027', label: 'Textile and electronics DPP deadlines confirmed' },
    ],
    problemHeadline: 'DPP is not optional — and the deadline is closer than you think',
    problemDescription: 'The EU ESPR regulation requires a Digital Product Passport for an expanding list of product categories. Each product needs machine-readable data on materials, carbon footprint, repairability, and recycling — accessible via QR code and connected to the EU central registry. Most manufacturers haven\'t started. The brands that build DPP infrastructure now will comply and capture customer relationships. The rest will scramble.',
    sections: [
      {
        title: 'Auto-generated DPP pages',
        description: 'BrandedMark generates compliant Digital Product Passport pages for every product — materials, carbon footprint, repairability scores, recycling instructions, and compliance documentation. All accessible via the same QR code that powers your product experience.',
        details: [
          '5-page DPP auto-generation per product',
          'Material composition and substances of concern',
          'Carbon footprint by lifecycle stage',
          'Repairability and durability scores',
          'Recycling and end-of-life instructions',
          'Anonymous REST endpoint for regulator access',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'GS1 Digital Link resolution',
        description: 'Your QR code isn\'t a proprietary redirect — it\'s a GS1 Digital Link that resolves through standards-based infrastructure. When the EU registry goes live in July 2026, your products are already compliant.',
        details: [
          'GS1 GTIN + Serial (SGTIN) encoding',
          'Standards-based URL resolution',
          'EU central registry connectivity',
          'Data persistence for 10+ years (product lifetime)',
          'No code expiration — your product identity is permanent',
        ],
        imagePosition: 'left' as const,
      },
      {
        title: 'Beyond compliance: the customer value layer',
        description: 'The same QR code that satisfies a regulator also serves the customer: warranty registration, setup guides, troubleshooting, spare parts, and support. One identifier, two audiences, compounding value.',
        details: [
          'Compliance data for regulators, experience for customers',
          'Warranty registration at the moment of DPP scan',
          'Support and spare parts accessible from the same code',
          'First-party customer data captured alongside compliance data',
        ],
        imagePosition: 'right' as const,
      },
    ],
    useCases: [
      { industry: 'Batteries', description: 'Enforced now — full DPP with 100+ data attributes, state-of-health tracking' },
      { industry: 'Textiles & Fashion', description: 'Confirmed 2027 — fiber composition, recycled content, care and repair instructions' },
      { industry: 'Electronics', description: 'Confirmed 2027 — material composition, repairability, energy efficiency data' },
      { industry: 'Furniture', description: 'Expected 2028 — material, durability, repairability scores, end-of-life handling' },
    ],
    ctaHeadline: 'Check your DPP readiness in 10 minutes',
    ctaDescription: 'Take the free DPP readiness scorecard — 10 questions, instant results, specific action plan.',
    relatedSlugs: ['qr-nfc-tags', 'analytics', 'warranty'],
  },
  {
    slug: 'post-purchase-experience',
    name: 'Post-Purchase Experience',
    tagline: 'From Sale to Loyalty.',
    headline: 'Turn every product into a revenue-generating relationship',
    subheadline: 'Warranty registration, AI support, spare parts commerce, and customer engagement — all powered by one QR scan. The post-purchase experience is where brand loyalty is built and aftermarket revenue is captured.',
    problemStats: [
      { value: '$15-$35', label: 'cost per support ticket that could be self-served' },
      { value: '60-80%', label: 'of product owners never register their warranty' },
      { value: '$47B', label: 'aftermarket parts revenue flowing to Amazon and third parties' },
    ],
    problemHeadline: 'After delivery, you lose the customer',
    problemDescription: 'The logistics industry calls tracking a package "post-purchase." It\'s not. Real post-purchase starts when the customer opens the box — and lasts years. Setup confusion drives returns. Unregistered warranties mean invisible customers. Support calls cost $15-$35 each when a QR scan could resolve them. And your spare parts revenue flows to Amazon because you have no direct channel to the product owner.',
    sections: [
      {
        title: 'Warranty registration in 10 seconds',
        description: 'Customer scans QR on the product → enters name and email → passkey authenticates → warranty activated. No paper cards, no apps, no passwords. Registration rates jump from 15% to 55%+.',
        details: [
          '10-second scan-to-register flow',
          'Passkey authentication (FIDO2/WebAuthn)',
          'Apple Wallet warranty card',
          '17+ jurisdiction-aware warranty rules',
          'Claims management with AI triage and fraud detection',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'AI-powered product support',
        description: 'An AI agent that knows your product manuals, your spare parts catalog, and this customer\'s specific warranty status. Not a chatbot — an agent that can create warranty claims, check parts availability, and schedule repairs.',
        details: [
          'RAG over product-specific documentation',
          'Tool use: create claims, order parts, schedule service',
          'Per-model troubleshooting (not generic FAQ)',
          'Auto-escalation to human agents when needed',
          'Resolution rate: 65-80% (vs 30-40% for generic chatbots)',
        ],
        imagePosition: 'left' as const,
      },
      {
        title: 'Spare parts and accessories commerce',
        description: 'Stop losing aftermarket revenue to Amazon. The QR on your product links directly to compatible spare parts and accessories — contextual, model-specific, one-click ordering.',
        details: [
          'Product-specific parts catalog per model',
          'One-click ordering with Stripe payments',
          'Contextual upsell at the right moment',
          'Extended warranty and service plan sales',
          '40-65% gross margin on direct parts',
        ],
        imagePosition: 'right' as const,
      },
    ],
    useCases: [
      { industry: 'Home Appliances', description: 'Filter replacement, maintenance reminders, warranty claims, extended service plans' },
      { industry: 'Power Tools', description: 'Blade and battery reorder, setup guides, safety documentation, consumable attach' },
      { industry: 'Consumer Electronics', description: 'Firmware updates, troubleshooting, accessory bundles, trade-in programs' },
      { industry: 'Outdoor & Sporting Goods', description: 'Care instructions, seasonal maintenance, ownership transfer for resale' },
    ],
    ctaHeadline: 'Start with one product. See results in 30 days.',
    ctaDescription: 'Generate QR codes for your highest-volume product line. Measure registration rate, support deflection, and parts revenue within the first month.',
    relatedSlugs: ['qr-nfc-tags', 'ai-support', 'spares-accessories'],
  },
  {
    slug: 'experience-designer',
    name: 'Experience Designer',
    tagline: 'Build Without Code.',
    headline: 'Design product experiences your customers actually use',
    subheadline: 'A no-code visual builder with 12 experience areas — setup guides, warranty registration, troubleshooting, spare parts, DPP data — all context-aware, versioned, and browser-based. No developers needed.',
    problemStats: [
      { value: '60%', label: 'of paper manuals are never opened' },
      { value: '73%', label: 'of customers prefer self-service to contacting support' },
      { value: '50+', label: 'products means 50+ custom pages — without a builder' },
    ],
    problemHeadline: 'Every product needs a digital experience. Most manufacturers can\'t build one.',
    problemDescription: 'Your customers scan a QR code and expect: setup instructions, warranty registration, troubleshooting, spare parts, and compliance data. Building this per product requires developers, costs months, and breaks when products change. An experience designer gives your product team the power to create, version, and publish these experiences — without touching code.',
    sections: [
      {
        title: 'Visual drag-and-drop builder',
        description: 'Design product experiences by dragging components onto a canvas. Add setup steps, warranty forms, troubleshooting flows, spare parts links, and DPP data pages. Configure conditional logic — show different content based on product, serial, warranty status, or language.',
        details: [
          '12 experience areas: Setup, Registration, Support, Spares, DPP, Care, Installation, Troubleshooting, Warranty, Accessories, Recall, End-of-Life',
          'Show/When conditional logic — display content based on product state',
          'Component library with shared elements across products',
          'Draft → Review → Published versioning with rollback',
          'Full change history and audit trail',
        ],
        imagePosition: 'right' as const,
      },
      {
        title: 'Context-aware by default',
        description: 'Every scan carries context: which product model, which serial number, what warranty status, what language, what country. The experience designer uses this context automatically — no configuration needed per user.',
        details: [
          'Auto-detected product, serial, and warranty status from QR data',
          'Language-aware content routing (multi-language support)',
          'Jurisdiction-aware warranty and compliance content',
          'Personalised spare parts catalog per product model',
          'Dynamic content based on registration state',
        ],
        imagePosition: 'left' as const,
      },
      {
        title: 'Guardrails that prevent chaos',
        description: 'Flexibility without guardrails creates 50 inconsistent product pages. Shared components (header, footer, branding) change once and update everywhere. Structured templates per experience area ensure consistency. Approval workflows prevent broken experiences from going live.',
        details: [
          'Shared brand components across all product experiences',
          'Structured templates per experience area (not blank canvases)',
          'Approval workflows before publishing',
          'Version comparison and rollback',
          'Preview mode before going live',
        ],
        imagePosition: 'right' as const,
      },
    ],
    useCases: [
      { industry: 'Product Teams', description: 'Build and iterate product experiences without engineering tickets or sprint planning' },
      { industry: 'After-Sales', description: 'Create troubleshooting flows and support pages that deflect tickets' },
      { industry: 'Compliance', description: 'Publish DPP data pages that meet EU requirements per product' },
      { industry: 'Marketing', description: 'Design branded unboxing experiences and registration flows that capture customer data' },
    ],
    ctaHeadline: 'Build your first product experience in minutes',
    ctaDescription: 'No code, no developers, no app downloads. Just the experience your customers deserve.',
    relatedSlugs: ['product-identity', 'post-purchase-experience', 'digital-product-passport'],
  },
];

export function getProductPage(slug: string): ProductPageData | undefined {
  return productPages.find(p => p.slug === slug);
}
